import type { components } from "@oathera/contract/api";
import { formatDateTime } from "../../lib/datetime.ts";
import type { ApiError } from "../../lib/api.ts";
import type { Measure, StatusLevel } from "../../components/index.ts";

/**
 * Presentation vocabulary for the System health screen (task 11.15; design 11 operator-API System
 * row `GET /system/health`, 12.2 "Control plane health", 12.3 principles, 12.4 a11y). This is the
 * single, testable home for:
 *
 * - the contract-derived types for the health snapshot — taken straight from `@oathera/contract/api`
 *   `components["schemas"]`, never redefined here (do NOT invent fields);
 * - the status-level mappings (icon+text, never colour alone; design 12.4) for the container state,
 *   the STATE GATEWAY state, and a per-batch PROCESSING FAILURE — none of which is ever "green"
 *   unless it is genuinely healthy (design 12.3: a temporarily-closed gateway, a waking container,
 *   a dead-letter batch are honestly surfaced as degraded/error, never green);
 * - the human labels and honest formatters the regions share;
 * - the error-to-sentence helper so a failed read reads plainly.
 *
 * The CRITICAL separation this screen enforces (design 5.3 / 6.1, FR-14.6 / FR-14.7): a batch's
 * `202` DURABLE QUEUE ACCEPTANCE is independent of its later PROCESSING OUTCOME. The contract models
 * this directly — `SystemHealth.queue` / `dead_letter` / `ingest` describe acceptance and movement,
 * and `SystemHealth.processing_failures` is a SEPARATE list of `ProcessingFailure`s keyed by
 * (`source_id`, `instance_id`, `batch_seq`) with `attempts`, `next_retry_at` and the terminal
 * `dead_lettered` flag. This file keeps the two vocabularies apart: there is no helper that folds a
 * processing outcome into the acceptance view, and vice versa.
 */

type Schemas = components["schemas"];

/* ---- Contract-derived types ----------------------------------------------------------------- */

/** The whole `GET /system/health` response (acceptance + runtime + the processing-failure page). */
export type SystemHealth = Schemas["SystemHealth"];
/** One processing failure, keyed by source/instance/batch-seq, SEPARATE from queue acceptance. */
export type ProcessingFailure = Schemas["ProcessingFailure"];
/** The per-batch processing lifecycle state (`queued`→`processing`→`retry`/`succeeded`/`dead`). */
export type BatchProcessingState = Schemas["BatchProcessingState"];
/** A number that says whether it is exact or an estimate. */
export type Quantity = Schemas["Quantity"];
/** The drop reason enum (the key set of `ingest.drops_by_reason`). */
export type DropReason = Schemas["enum_drop_reason"];

/** The container-state sub-object. */
export type ContainerState = SystemHealth["container"];
/** The State-Gateway sub-object (open vs temporarily_closed). */
export type GatewayState = SystemHealth["state_gateway"];

/* ---- Quantity → confidence ------------------------------------------------------------------ */

/**
 * Map a `Quantity`'s exactness onto the shared exact/estimate vocabulary (design 12.3), matching the
 * other screens. A quantity with no data cannot be verified — neither an exact counter nor a sampled
 * estimate — so it is `unverifiable` and never rendered green.
 */
export function quantityMeasure(q: Quantity): Measure {
  if (q.value === null) return "unverifiable";
  return q.exactness === "estimated" ? "estimate" : "exact";
}

/** A `Quantity`'s numeric value, treating a null (no-data) as the sentinel the formatter shows. */
export function quantityValue(q: Quantity): number | null {
  return q.value;
}

/* ---- Container state (icon + text; waking/unavailable are never green) ---------------------- */

/** Status level for the container state. Only `ready` is healthy; waking is a warn, unavailable error. */
export function containerLevel(state: ContainerState["state"]): StatusLevel {
  switch (state) {
    case "ready":
      return "healthy";
    case "waking":
      return "warn";
    case "unavailable":
      return "error";
  }
}

export function containerStateLabel(state: ContainerState["state"]): string {
  switch (state) {
    case "ready":
      return "Ready";
    case "waking":
      return "Waking";
    case "unavailable":
      return "Unavailable";
  }
}

/* ---- State Gateway (the honest "temporarily closed" state; design 6.4.1 / 12.3) ------------- */

/**
 * Status level for the State Gateway. An OPEN gateway is healthy. A TEMPORARILY-CLOSED gateway is a
 * VALID DEGRADED state surfaced honestly (design 6.4.1): it is a `warn`, NOT an `error` (nothing has
 * failed) and NOT `healthy`/green (it is not fully available). This is the keystone honesty rule of
 * the gateway tile — a temporarily-closed gateway can never read as "all good".
 */
export function gatewayLevel(state: GatewayState["state"]): StatusLevel {
  switch (state) {
    case "open":
      return "healthy";
    case "temporarily_closed":
      return "warn";
  }
}

export function gatewayStateLabel(state: GatewayState["state"]): string {
  switch (state) {
    case "open":
      return "Open";
    case "temporarily_closed":
      return "Temporarily closed";
  }
}

/* ---- Processing failures (SEPARATE from acceptance; keyed source/instance/seq) -------------- */

/**
 * Status level for ONE processing failure's lifecycle state. A terminal DEAD-LETTER batch is the
 * worst (an `error`); a batch in RETRY is a `warn`; `queued`/`processing` are in-flight (`neutral`,
 * not green — nothing has succeeded yet); `succeeded` would be the only healthy state but such a
 * batch is not a "failure" and the server does not list it here. The `dead_lettered` flag is the
 * authority for the terminal state even if `state` lagged, so it is checked first.
 */
export function failureLevel(failure: ProcessingFailure): StatusLevel {
  if (failure.dead_lettered || failure.state === "dead") return "error";
  switch (failure.state) {
    case "retry":
      return "warn";
    case "succeeded":
      return "healthy";
    case "queued":
    case "processing":
      return "neutral";
  }
}

/** The visible lifecycle label for a processing failure (icon+text, not colour alone). */
export function failureStateLabel(failure: ProcessingFailure): string {
  if (failure.dead_lettered) return "Dead-lettered";
  switch (failure.state) {
    case "queued":
      return "Queued";
    case "processing":
      return "Processing";
    case "retry":
      return "Retry";
    case "succeeded":
      return "Succeeded";
    case "dead":
      return "Dead-lettered";
  }
}

/**
 * A longer accessible phrase for a processing failure's state, so assistive tech distinguishes a
 * batch IN RETRY (will be re-attempted) from a TERMINAL dead-letter (no further attempts) by text,
 * not colour alone (design 12.4).
 */
export function failureStateTitle(failure: ProcessingFailure): string {
  if (failure.dead_lettered || failure.state === "dead") {
    return "Terminal dead-letter: no further attempts will be made for this batch.";
  }
  switch (failure.state) {
    case "retry":
      return failure.next_retry_at === null
        ? "In retry: a further attempt is scheduled."
        : `In retry: next attempt at ${formatDateTime(failure.next_retry_at)}.`;
    case "queued":
      return "Queued for processing: not yet attempted.";
    case "processing":
      return "Processing: an attempt is in flight.";
    case "succeeded":
      return "Processed successfully.";
  }
}

/** The drill-down target for a processing failure: this batch's source + instance in the events view. */
export function failureDrillTo(failure: ProcessingFailure): string {
  const params = new URLSearchParams();
  params.set("source", failure.source_id);
  params.set("instance", failure.instance_id);
  return `/events?${params.toString()}`;
}

/* ---- Honest formatters ---------------------------------------------------------------------- */

/** Format an epoch-ms timestamp as a local time, or "n/a" when null/unknown. */
export function formatTime(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return "n/a";
  return new Date(ms).toLocaleString();
}

/** Format a seconds age/latency as a short human span, or "n/a" when null (no data; never 0-as-fact). */
export function formatAgeSeconds(totalS: number | null): string {
  if (totalS === null || !Number.isFinite(totalS) || totalS < 0) return "n/a";
  const s = Math.floor(totalS);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} m ${String(s % 60).padStart(2, "0")} s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ${String(m % 60).padStart(2, "0")} m`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH === 0 ? `${d} d` : `${d} d ${String(remH).padStart(2, "0")} h`;
}

/** Format a count exactly (counters are exact; design 12.3). */
export function formatCount(n: number): string {
  if (!Number.isFinite(n)) return "n/a";
  return n.toLocaleString();
}

/** Format a rate (per second) to a short precision, or "n/a" when null. */
export function formatRate(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "n/a";
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}/s`;
}

/** Format a millisecond latency, or "n/a" when null. */
export function formatMillis(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "n/a";
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ms`;
}

/** Human label for a drop reason (the enum is also used by the Sources/ingest views). */
export function dropReasonLabel(reason: DropReason): string {
  switch (reason) {
    case "unknown_event":
      return "Unknown event";
    case "unknown_metric":
      return "Unknown metric";
    case "emitter_mismatch":
      return "Emitter mismatch";
    case "attr_rejected":
      return "Attribute rejected";
    case "event_rejected":
      return "Event rejected";
    case "label_rejected":
      return "Label rejected";
    case "series_cap":
      return "Series cap";
    case "enum_mapped":
      return "Enum remapped";
    case "credential_shaped":
      return "Credential-shaped value";
    case "buffer_full":
      return "Buffer full";
    case "breaker_open":
      return "Breaker open";
    case "shutdown":
      return "Shutdown";
    case "other":
      return "Other";
  }
}

/* ---- Error → sentence ----------------------------------------------------------------------- */

/** Turn a failed READ into an operator-facing sentence (mirrors the other screens' read map). */
export function readErrorMessage(error: ApiError | null): string {
  if (error === null) return "The control-plane health view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see control-plane health. The server enforces access.";
    case "network":
      return "The health request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}
